import React, { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  CheckCircle, ArrowRight, Shield, Zap, Clock, FileText,
  Star, Smartphone, CreditCard, Send, Receipt
} from 'lucide-react';
import { analytics } from '../../lib/ga4';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IMAGES = {
  freelance: 'https://static.prod-images.emergentagent.com/jobs/e195386f-f80b-4538-af09-25780105987d/images/56d0e3e01b80a87abda8b3ad32c5cdff1fa09ecc6d2c4175469376dac195660a.png'
};

const LandingFreelance = () => {
  const [searchParams] = useSearchParams();
  
  useEffect(() => {
    const utmSource = searchParams.get('utm_source');
    analytics.trackEvent('landing_view', {
      page: 'freelance',
      utm_source: utmSource
    });

    fetch(`${API_URL}/api/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'landing_view',
        details: { page: 'freelance', referrer: document.referrer }
      })
    }).catch(() => {});
  }, [searchParams]);

  const handleCTAClick = (buttonName) => {
    analytics.trackButtonClick(buttonName, 'landing_freelance');
  };

  return (
    <>
      <Helmet>
        <title>Logiciel Facturation Freelance | Factures Pro en 2 min | SmartFacture AI</title>
        <meta name="description" content="Le meilleur logiciel de facturation pour freelances. Créez des factures professionnelles en 2 minutes. Conforme 2026. Essai gratuit. À partir de 14€/mois." />
        <link rel="canonical" href="https://smartfacture-ai.com/landing/freelance" />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="bg-white border-b border-slate-100 py-4 px-4">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <span className="font-bold text-xl text-slate-900">SmartFacture AI</span>
            </Link>
            <Link 
              to="/register"
              onClick={() => handleCTAClick('header_cta')}
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
            >
              Essai gratuit
            </Link>
          </div>
        </header>

        {/* Hero */}
        <section className="bg-gradient-to-br from-emerald-600 to-teal-700 text-white py-16 lg:py-24">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/20 rounded-full text-sm mb-6">
                  <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  N°1 des freelances
                </div>
                
                <h1 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                  Facturation freelance :
                  <span className="text-emerald-200"> créez vos factures en 2 minutes</span>
                </h1>
                
                <p className="text-xl text-emerald-100 mb-8">
                  Fini Excel et Word. Créez des factures <strong>professionnelles</strong> depuis votre téléphone, conformes à la réforme 2026.
                </p>

                <div className="space-y-3 mb-8">
                  {[
                    'Factures pro en 2 minutes chrono',
                    'Devis convertibles en factures',
                    'Relances automatiques des impayés',
                    'Conforme Factur-X 2026'
                  ].map((benefit, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-emerald-300 flex-shrink-0" />
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Link 
                    to="/register"
                    onClick={() => handleCTAClick('hero_cta')}
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-emerald-700 hover:bg-emerald-50 text-lg font-semibold rounded-xl shadow-xl transition-all"
                  >
                    Créer ma première facture
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                </div>

                <p className="text-sm text-emerald-200 mt-4">
                  Essai gratuit 7 jours • Sans carte bancaire
                </p>
              </div>

              <div className="hidden lg:block">
                <img 
                  src={IMAGES.freelance}
                  alt="Freelance utilisant SmartFacture AI"
                  className="rounded-2xl shadow-2xl"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Pain Points */}
        <section className="py-16 px-4 bg-slate-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                On connaît vos galères de freelance
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  icon: Clock,
                  problem: 'Perte de temps',
                  solution: 'Facture créée en 2 min depuis l\'app mobile'
                },
                {
                  icon: CreditCard,
                  problem: 'Clients qui ne paient pas',
                  solution: 'Relances automatiques programmées'
                },
                {
                  icon: Shield,
                  problem: 'Peur de la réforme 2026',
                  solution: 'Factur-X conforme automatiquement'
                }
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 shadow-sm">
                  <item.icon className="h-10 w-10 text-emerald-600 mb-4" />
                  <h3 className="font-semibold text-slate-900 mb-2">"{item.problem}"</h3>
                  <p className="text-emerald-600 font-medium">→ {item.solution}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">
              Tout ce dont un freelance a besoin
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Receipt, title: 'Factures pro', desc: 'Templates élégants personnalisables' },
                { icon: Send, title: 'Devis en 1 clic', desc: 'Conversion devis → facture automatique' },
                { icon: Smartphone, title: 'App mobile', desc: 'Facturez depuis n\'importe où' },
                { icon: Zap, title: 'Relances auto', desc: 'Ne perdez plus d\'argent' }
              ].map((feature, i) => (
                <div key={i} className="text-center p-6 rounded-2xl bg-slate-50">
                  <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="h-7 w-7 text-emerald-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600 text-sm">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="py-16 px-4 bg-emerald-600 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">
              Tarif freelance : 14€/mois
            </h2>
            <p className="text-xl text-emerald-100 mb-8">
              30 factures/mois • Devis illimités • Relances incluses
            </p>

            <div className="bg-white rounded-2xl p-8 text-slate-900 max-w-md mx-auto mb-8">
              <p className="text-4xl font-bold text-emerald-600">14€<span className="text-lg font-normal text-slate-500">/mois</span></p>
              <p className="text-slate-600 mb-6">Forfait Starter</p>
              <ul className="text-left space-y-3 mb-6">
                {[
                  '20 factures par mois',
                  'Devis illimités',
                  'Format Factur-X',
                  'Relances automatiques',
                  'Export comptable',
                  'Support email'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-emerald-500" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link 
                to="/register"
                onClick={() => handleCTAClick('pricing_cta')}
                className="block w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl text-center transition-colors"
              >
                Essayer gratuitement
              </Link>
            </div>

            <p className="text-emerald-200 text-sm">
              Besoin de plus ? Forfaits Pro (29€) et Expert (49€) disponibles.
            </p>
          </div>
        </section>

        {/* Testimonial */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-slate-50 rounded-2xl p-8 lg:p-12">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <blockquote className="text-xl lg:text-2xl text-slate-700 mb-6">
                "En tant que développeur freelance, je perdais un temps fou sur mes factures. Avec SmartFacture AI, je crée ma facture en 2 min depuis mon téléphone juste après une réunion client. Game changer !"
              </blockquote>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                  <span className="text-emerald-600 font-bold">TM</span>
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Thomas M.</p>
                  <p className="text-slate-500">Développeur web freelance</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 px-4 bg-slate-900 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Créez votre première facture en 2 minutes
            </h2>
            <p className="text-xl text-slate-300 mb-8">
              Rejoignez les freelances qui ont déjà simplifié leur facturation
            </p>
            
            <Link 
              to="/register"
              onClick={() => handleCTAClick('final_cta')}
              className="inline-flex items-center justify-center gap-2 px-10 py-5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white text-xl font-semibold rounded-xl shadow-xl transition-all"
            >
              Démarrer gratuitement
              <ArrowRight className="h-6 w-6" />
            </Link>

            <div className="flex items-center justify-center gap-6 mt-8 text-sm text-slate-400">
              <span className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-emerald-400" />
                7 jours gratuits
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-emerald-400" />
                Sans engagement
              </span>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-950 text-slate-400 py-8 px-4">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <p>© 2026 SmartFacture AI. Tous droits réservés.</p>
            <div className="flex gap-6 text-sm">
              <Link to="/mentions-legales" className="hover:text-white">Mentions légales</Link>
              <Link to="/cgu" className="hover:text-white">CGU</Link>
              <Link to="/confidentialite" className="hover:text-white">Confidentialité</Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default LandingFreelance;
